import { CanceledError } from 'axios';

import { QueryParams } from '@/common/models';
import Unit from '@/common/models/unit/Unit';
import UnitFallbackCreative from '@/common/models/unit/UnitFallbackCreative';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import * as modelTypes from '@/store/modules/models/mutation-types';
import { createApiAction, createPatchAction } from '@/utils/ActionsUtils';
import { IPaginationResult } from '@/utils/ApiUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

export default {
  async uploadImage({ commit }, { unitId, unitImage }) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    const result = API.instance.putUnitImage(unitId, unitImage).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToUploadImage');
      ErrorHandler.showError(msg, error);
      GlobalEventBus.$emit(GlobalEvents.loaded);
      throw error;
    });
    GlobalEventBus.$emit(GlobalEvents.loaded);
    return result;
  },

  createNewUnit({ commit }, unit = new Unit(-1, '')) {
    commit(types.CREATE_UNIT, unit);
  },

  deleteUnits({ commit }, units) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfulApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = (error) => {
        if (successfulApiCalls + failedApiCalls === units.length) {
          if (successfulApiCalls === units.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject(error);
            const msg = i18n.global.t('messages.failedToDeleteUnits');
            ErrorHandler.showError(msg, error);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      units.forEach((unit) => {
        API.instance.deleteUnit(
          unit.id,
          () => {
            commit(types.DELETE_UNIT, unit);
            successfulApiCalls += 1;
            evalCompletion();
          },
          (error) => {
            failedApiCalls += 1;
            evalCompletion(error);
          },
        );
      });
    });
  },

  deleteUnit({ commit }, unit) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deleteUnit(
        unit.id,
        () => {
          commit(types.DELETE_UNIT, unit);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeleteUnit');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getCreativesPerUnit({ commit }, queryParams) {
    return new Promise((resolve, reject) => {
      if (queryParams.showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      API.instance.getCreativesPerUnit(
        queryParams,
        (result) => {
          resolve(result);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetUnitFallbackCreatives');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (queryParams.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  patchUnits: createPatchAction(API.instance.patchUnits),
  getUnits({ commit }, params: { queryParams: QueryParams; showLoader?: boolean }): Promise<IPaginationResult<Unit>> {
    return new Promise((resolve, reject) => {
      if (params.showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      API.instance.getUnits(
        params,
        (units) => {
          commit(types.UPDATE_UNITS_LIST, units.items);
          resolve(units);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetUnits');
          ErrorHandler.showError(msg, error);
          reject(error);
          if (params.showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  async getUnitsMinimal({ commit }, { queryParams, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance.getUnitsMinimal(queryParams).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetUnits');
      ErrorHandler.showError(msg, error);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw error;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  getUnitsInGeofences: createApiAction(API.instance.getUnitsInGeofences, null, 'messages.failedToGetUnits', true),
  getUnitIDsInGeofences: createApiAction(API.instance.getUnitIDsInGeofences, null, 'messages.failedToGetUnits', true),

  async getClusteredUnits(
    { commit },
    { queryParams, minLat, maxLat, minLng, maxLng, zoom, cancelTokenSource, unitIDs, showLoader = true },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    try {
      return await API.instance.getClusteredUnits({
        queryParams,
        minLat,
        maxLat,
        minLng,
        maxLng,
        zoom,
        cancelTokenSource,
        unitIDs,
      });
    } catch (error) {
      if (error != null) {
        const msg = i18n.global.t('messages.failedToGetUnits');
        ErrorHandler.showError(msg, error);
        throw error;
      }
    } finally {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
    }
  },

  async getClusteredUnitsFromTargets(
    { commit },
    {
      params,
      minLat,
      maxLat,
      minLng,
      maxLng,
      zoom,
      unitIDs,
      type,
      cancelTokenSource,
      showLoader = true,
      isPrivate = true,
      planUuid = null,
      withUnitIds = false,
      withMediaOwnerInformation = false,
    },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    try {
      return await API.instance.getClusteredUnitsFromTargets({
        params,
        minLat,
        maxLat,
        minLng,
        maxLng,
        zoom,
        cancelTokenSource,
        unitIDs,
        type,
        isPrivate,
        planUuid,
        withUnitIds,
        withMediaOwnerInformation,
      });
    } catch (error) {
      if (error != null && !(error instanceof CanceledError)) {
        const msg = i18n.global.t('messages.failedToGetUnits');
        ErrorHandler.showError(msg, error);
        throw error;
      }
    } finally {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
    }
  },

  async getUnitsForPublicPlan({ commit }, params) {
    const { cancelTokenSource, showLoader, planUuid, queryParams } = params;
    if (showLoader) GlobalEventBus.$emit(GlobalEvents.loading);

    try {
      return await API.instance.getUnitsForPublicPlan({
        params: queryParams.toObject(),
        cancelTokenSource,
        showLoader,
        planUuid,
      });
    } catch (error) {
      if (error != null) {
        const msg = i18n.global.t('messages.failedToGetUnits');
        ErrorHandler.showError(msg, error);
        throw error;
      }
    } finally {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
    }
  },

  async getClusteredSiteNetworksFromTargets(
    { commit },
    {
      params,
      minLat,
      maxLat,
      minLng,
      maxLng,
      zoom,
      type,
      cancelTokenSource,
      showLoader = true,
      isPrivate = true,
      planUuid = null,
    },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    try {
      return await API.instance.getClusteredSiteNetworksFromTargets({
        params,
        minLat,
        maxLat,
        minLng,
        maxLng,
        zoom,
        cancelTokenSource,
        type,
        isPrivate,
        planUuid,
      });
    } catch (error) {
      if (error != null) {
        const msg = i18n.global.t('messages.failedToGetUnits');
        ErrorHandler.showError(msg, error);
        throw error;
      }
    } finally {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
    }
  },

  async getUnitAudienceData({ commit }, params: { id: string; fromDate: string; toDate: string }, showLoader = false) {
    try {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      return await API.instance.getUnitAudienceData(params).catch(({ error }) => {
        const msg = i18n.global.t('messages.failedToGetUnitsAudienceData');
        ErrorHandler.showError(msg, error);
        throw error;
      });
    } finally {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
  },

  // params = { ids, fromDate, toDate, demographicIDs }
  async getUnitsAudienceData(
    { commit },
    { ids, fromDate, toDate, demographicIDs, showLoader = true, cancelTokenSource = null, progressCallback = null },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance
      .getUnitsAudienceData({
        ids,
        fromDate,
        toDate,
        demographicIDs,
        cancelTokenSource,
        progressCallback,
      })
      .catch(({ error }) => {
        if (error) {
          const msg = i18n.global.t('messages.failedToGetUnitsAudienceData');
          ErrorHandler.showError(msg, error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
          throw error;
        }
      });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  async getUnitsHourlyImpressions({ commit }, params) {
    return API.instance.getUnitsHourlyImpressions(params).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetUnitsHourlyImpressions');
      ErrorHandler.showError(msg, error);
      throw error;
    });
  },

  getUnit({ commit }, id, showLoader = true) {
    return new Promise((resolve, reject) => {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      API.instance.getUnit(
        id,
        (unit) => {
          commit(types.UPDATE_UNIT, unit);
          resolve(unit);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetUnit');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getUnitsBrief({ commit }, unitIds) {
    return new Promise((resolve, reject) => {
      API.instance.getUnitsBrief(
        unitIds,
        (result) => {
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetUnits');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getUnitsBriefUsingPost({ commit }, { unitIDs, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance.getUnitsBriefUsingPost(unitIDs).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetUnits');
      ErrorHandler.showError(msg, error);
      GlobalEventBus.$emit(GlobalEvents.loaded);
      throw error;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  linkFallbackCreativesToUnit({ commit }, change, showLoader = true) {
    return new Promise((resolve, reject) => {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      const { unit, creatives } = change;
      const fallbackCreativeUnits = creatives.map((x) => new UnitFallbackCreative(-1, x.id, unit.id));
      API.instance.postUnitFallbackCreatives(
        fallbackCreativeUnits,
        (result) => {
          // Combine existing fallback creatives with new ones
          const updatedFallbackCreatives = [...unit.fallbackCreatives, ...result];
          commit(modelTypes.UPDATE_UNIT_FALLBACK_CREATIVES, { unit, value: updatedFallbackCreatives }, { root: true });
          GlobalEventBus.$emit(GlobalEvents.loaded);
          resolve(result);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToLinkFallbackCreativesToUnit');
          ErrorHandler.showError(msg, error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
          reject(error);
        },
      );
    });
  },

  saveUnit({ commit }, { unit, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise<void>((resolve, reject) => {
      const successCallback = (data) => {
        commit(types.SAVE_UNIT, data);
        resolve(data);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSaveUnit');
        ErrorHandler.showError(msg, error);
        reject(error);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      if (unit.id === -1) {
        API.instance.postUnit(unit, successCallback, errorCallback);
      } else {
        API.instance.putUnit(unit, successCallback, errorCallback);
      }
    });
  },

  deleteUnitFallbackCreatives({ commit }, unitFallbackCreatives) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = (error) => {
        if (successfullApiCalls + failedApiCalls === unitFallbackCreatives.length) {
          if (successfullApiCalls === unitFallbackCreatives.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject(error);
            const msg = i18n.global.t('messages.failedToDeleteUnitFallbackCreatives');
            ErrorHandler.showError(msg, error);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      unitFallbackCreatives.forEach((unitFallbackCreative) => {
        API.instance.deleteUnitFallbackCreatives(
          unitFallbackCreative,
          () => {
            successfullApiCalls += 1;
            evalCompletion();
          },
          (error) => {
            failedApiCalls += 1;
            evalCompletion(error);
          },
        );
      });
    });
  },

  async getUnitAudit({ commit }, { id, cancelTokenSource, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const result = await API.instance.getUnitAudit({ id, cancelTokenSource }).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetUnitAudit');
      ErrorHandler.showError(msg, error);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw error;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  async getUnitAdRequestPerHour({ commit }, { id, params, cancelTokenSource, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const result = await API.instance.getUnitAdRequestPerHour({ id, params, cancelTokenSource }).catch(({ error }) => {
      const msg = i18n.global.t('messages.failedToGetUnitAudit');
      ErrorHandler.showError(msg, error);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw error;
    });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return result;
  },

  async getUnitFillRate({ commit }, params: { id: string; fromDate: string; toDate: string }, showLoader = false) {
    try {
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loading);
      }
      return await API.instance.getUnitFillRate(params).catch(({ error }) => {
        const msg = i18n.global.t('messages.failedToGetUnitsAudienceData');
        ErrorHandler.showError(msg, error);
        throw error;
      });
    } finally {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
  },

  saveUnitProperty({ commit }, { unitProperty, unitId }) {
    GlobalEventBus.$emit(GlobalEvents.loading);

    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        resolve(data);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSaveUnitProperty');
        ErrorHandler.showError(msg, error);
        reject(error);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };

      if (unitProperty.unitproperty_id === -1) {
        API.instance.postUnitProperty({ ...unitProperty, unit_id: unitId }, successCallback, errorCallback);
      } else if (unitProperty.value === '' || unitProperty.value == null) {
        API.instance.deleteUnitProperty(unitProperty.unitproperty_id, successCallback, errorCallback);
      } else {
        API.instance.putUnitProperty(
          unitProperty.unitproperty_id,
          { ...unitProperty, unit_id: unitId },
          successCallback,
          errorCallback,
        );
      }
    });
  },
};
